/**
 * SyncManagerPage (ADR-018) — Tour de contrôle par source de données.
 * Affiche SOFI, Geotab, JPL, ProFuel, SAAQ au lieu des apps GS.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@gs/auth';
import { ApiServerError } from '@gs/api-client';
import { SyncStatusPanel } from '../components/SyncStatusPanel';
import {
  runningSourceKeyFromLog,
  SYNC_RUNNING_QUERY_KEY,
  type SyncRunningStatusResponse,
  useSyncRunningStatus,
} from '../hooks/useSyncRunningStatus';
import { api } from '../services/api';

interface SourceRow {
  sourceKey: string;
  sourceName: string;
  sourceType: string;
  frequency: string;
  isEnabled: boolean;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastRunMs: number | null;
  lastRunCount: number | null;
}

/** Sans `running` confirmé (queue prod ~30 s + marge job Container App). */
const QUEUE_WAIT_TIMEOUT_MS = 90_000;

type RunNoticeKind = 'started' | 'queued' | 'busy' | 'error' | 'skipped';

type PendingTrigger = {
  sourceKey: string;
  sinceMs: number;
  mode: 'started' | 'queued';
};

const FREQ_LABELS: Record<string, string> = {
  hourly: '1 h',
  nightly: 'Nuit',
  weekly: 'Semaine',
  monthly: 'Mois',
  manual: 'Manuel',
  realtime: 'Temps réel',
};
const DEFAULT_FREQS = ['hourly', 'nightly', 'weekly', 'monthly', 'manual'];

const SOURCE_TYPE_STYLE: Record<string, string> = {
  sql_server: 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300',
  api_rest: 'bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300',
  sftp: 'bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300',
  oauth: 'bg-teal-100 dark:bg-teal-950/40 text-teal-700 dark:text-teal-300',
};

function StatusPill({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-slate-400 dark:text-slate-500">—</span>;
  const styles: Record<string, string> = {
    ok: 'bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400',
    partial: 'bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-400',
    error: 'bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400',
    running: 'bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400',
    skipped: 'bg-slate-100 dark:bg-slate-800 text-slate-500',
  };
  const labels: Record<string, string> = {
    ok: 'OK',
    partial: 'Partiel',
    error: 'Erreur',
    running: 'En cours',
    skipped: 'Ignoré',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] ?? styles.skipped}`}
    >
      {labels[status] ?? status}
    </span>
  );
}

function RunButton({
  label,
  onClick,
  disabled,
  variant = 'primary',
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  variant?: 'primary' | 'ghost';
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`touch-manipulation inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        variant === 'primary'
          ? 'bg-brand-500 hover:bg-brand-600 text-white'
          : 'border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
      }`}
    >
      <svg viewBox="0 0 10 10" className="w-2.5 h-2.5 fill-current">
        <polygon points="2,1 9,5 2,9" />
      </svg>
      {label}
    </button>
  );
}

function logStartedAfterTrigger(logStartedAt: string, triggerSinceMs: number): boolean {
  return new Date(logStartedAt).getTime() >= triggerSinceMs - 2_000;
}

export default function SyncManagerPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const waitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'sources'],
    queryFn: () => api.get<SourceRow[]>('/api/syncs/sources'),
    enabled: !!user,
  });

  const [pendingTrigger, setPendingTrigger] = useState<PendingTrigger | null>(null);
  const [runNotice, setRunNotice] = useState<{ kind: RunNoticeKind; text: string } | null>(null);

  const { data: runningStatus } = useSyncRunningStatus(!!pendingTrigger);

  const runningSourceKey = runningSourceKeyFromLog(runningStatus?.log);

  const pendingLog = runningStatus?.log;
  const pendingLogSourceKey = pendingLog ? pendingLog.source_key ?? pendingLog.app_key : null;

  // Ne pas confondre le dernier run terminé (log stale) avec la fin du run qu'on attend :
  // seul un log démarré APRÈS le clic compte comme « settled » (critique en mode queue).
  const pendingRunSettled =
    pendingTrigger !== null &&
    pendingLogSourceKey === pendingTrigger.sourceKey &&
    pendingLog?.status !== 'running' &&
    pendingLog?.started_at != null &&
    logStartedAfterTrigger(pendingLog.started_at, pendingTrigger.sinceMs);

  const awaitingRunKey =
    pendingTrigger !== null &&
    runningSourceKey !== pendingTrigger.sourceKey &&
    !pendingRunSettled
      ? pendingTrigger.sourceKey
      : null;
  const anySyncActive = runningSourceKey !== null || awaitingRunKey !== null;

  const runningConfirmedForPending =
    pendingTrigger !== null && runningSourceKey === pendingTrigger.sourceKey;

  const visibleRunNotice =
    runNotice &&
    !(
      runningConfirmedForPending &&
      (runNotice.kind === 'started' || runNotice.kind === 'queued')
    )
      ? runNotice
      : null;

  useEffect(() => {
    return () => {
      if (waitTimeoutRef.current) clearTimeout(waitTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (runningConfirmedForPending && waitTimeoutRef.current) {
      clearTimeout(waitTimeoutRef.current);
      waitTimeoutRef.current = null;
    }
  }, [runningConfirmedForPending]);

  const sources = data ?? [];

  const scheduleWaitTimeout = (sourceKey: string, sourceName: string) => {
    if (waitTimeoutRef.current) clearTimeout(waitTimeoutRef.current);
    waitTimeoutRef.current = setTimeout(() => {
      waitTimeoutRef.current = null;
      const data = qc.getQueryData<SyncRunningStatusResponse>(SYNC_RUNNING_QUERY_KEY);
      const activeKey = runningSourceKeyFromLog(data?.log);
      if (activeKey === sourceKey) return;

      setPendingTrigger((current) => {
        if (current?.sourceKey !== sourceKey) return current;
        setRunNotice({
          kind: 'error',
          text: `${sourceName} — le worker n'a pas démarré, réessayer ?`,
        });
        return null;
      });
    }, QUEUE_WAIT_TIMEOUT_MS);
  };

  const patchSource = useMutation({
    mutationFn: ({
      sourceKey,
      body,
    }: {
      sourceKey: string;
      body: { frequency?: string; isEnabled?: boolean };
    }) => api.patch(`/api/syncs/sources/${sourceKey}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'sources'] }),
  });

  const runSource = useMutation<
    { ok: boolean; logId?: string; skipped?: boolean; status?: string },
    Error,
    string
  >({
    mutationFn: (sourceKey) => api.post(`/api/syncs/sources/${sourceKey}/run`, {}),
    onMutate: (sourceKey) => {
      if (waitTimeoutRef.current) clearTimeout(waitTimeoutRef.current);
      setPendingTrigger({ sourceKey, sinceMs: Date.now(), mode: 'started' });
      setRunNotice(null);
    },
    onSuccess: (result, sourceKey) => {
      const name = sources.find((s) => s.sourceKey === sourceKey)?.sourceName ?? sourceKey;

      if (result.skipped) {
        setPendingTrigger(null);
        setRunNotice({
          kind: 'skipped',
          text: `${name} — extracteur non implémenté, run marqué ignoré`,
        });
      } else if (result.status === 'queued') {
        setPendingTrigger((prev) =>
          prev?.sourceKey === sourceKey ? { ...prev, mode: 'queued' } : prev,
        );
        setRunNotice({
          kind: 'queued',
          text: `${name} — demande mise en file, démarrage par le worker`,
        });
        scheduleWaitTimeout(sourceKey, name);
      } else {
        setPendingTrigger((prev) =>
          prev?.sourceKey === sourceKey ? { ...prev, mode: 'started' } : prev,
        );
        setRunNotice({
          kind: 'started',
          text: `${name} — Demande envoyée — démarrage`,
        });
        scheduleWaitTimeout(sourceKey, name);
      }

      qc.invalidateQueries({ queryKey: ['admin', 'sources'] });
      qc.invalidateQueries({ queryKey: ['admin', 'logs'] });
      qc.invalidateQueries({ queryKey: SYNC_RUNNING_QUERY_KEY });
    },
    onError: (err) => {
      if (waitTimeoutRef.current) clearTimeout(waitTimeoutRef.current);
      setPendingTrigger(null);
      const isBusy = err instanceof ApiServerError && err.status === 409;
      setRunNotice(
        isBusy
          ? { kind: 'busy', text: 'Un sync est déjà en cours' }
          : { kind: 'error', text: err.message || 'Échec du déclenchement.' },
      );
      qc.invalidateQueries({ queryKey: SYNC_RUNNING_QUERY_KEY });
    },
  });

  return (
    <div className="space-y-5">
      <SyncStatusPanel
        onDone={() => {
          qc.invalidateQueries({ queryKey: ['admin', 'sources'] });
          qc.invalidateQueries({ queryKey: ['admin', 'logs'] });
        }}
      />

      {visibleRunNotice && (
        <div
          role="status"
          className={`flex items-start justify-between gap-3 rounded-xl border px-4 py-2.5 text-sm ${
            visibleRunNotice.kind === 'error'
              ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400'
              : visibleRunNotice.kind === 'busy'
                ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400'
                : visibleRunNotice.kind === 'skipped'
                  ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 text-slate-600 dark:text-slate-400'
                  : 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-400'
          }`}
        >
          <span>{visibleRunNotice.text}</span>
          <button
            type="button"
            onClick={() => setRunNotice(null)}
            className="touch-manipulation shrink-0 text-xs underline opacity-70 hover:opacity-100"
          >
            Fermer
          </button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Sources de données</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {sources.filter((s) => s.isEnabled).length} sources actives · Tour de contrôle ETL (ADR-018)
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isLoading}
          className="touch-manipulation text-xs px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 transition-colors"
        >
          Actualiser
        </button>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
          ))}
        </div>
      )}

      {isError && (
        <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-4 text-sm text-red-700 dark:text-red-400">
          Erreur de chargement.{' '}
          <button type="button" className="underline touch-manipulation" onClick={() => refetch()}>
            Réessayer
          </button>
        </div>
      )}

      <div className="space-y-3">
        {sources.map((source) => {
          const isRunning = runningSourceKey === source.sourceKey;
          const isImplemented = ['sofi', 'azure_ad'].includes(source.sourceKey);
          const isPending = awaitingRunKey === source.sourceKey;
          const pendingMode =
            isPending && pendingTrigger?.sourceKey === source.sourceKey
              ? pendingTrigger.mode
              : null;
          const cardShowsRunning = isRunning || isPending;
          const displayStatus = cardShowsRunning ? 'running' : source.lastRunStatus;

          const pendingBadgeLabel =
            pendingMode === 'queued' ? 'En file…' : 'Démarrage…';
          const buttonLabel = isPending
            ? pendingMode === 'queued'
              ? 'En file…'
              : 'Envoi…'
            : isRunning
              ? 'En cours…'
              : 'Synchroniser';

          return (
            <div
              key={source.sourceKey}
              className={`rounded-xl border bg-white dark:bg-slate-900 p-4 shadow-xs ${
                cardShowsRunning
                  ? 'border-blue-200 dark:border-blue-800'
                  : 'border-slate-200 dark:border-slate-700'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="min-w-40">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-slate-800 dark:text-slate-100">{source.sourceName}</span>
                    {isRunning && (
                      <span className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
                        En cours…
                      </span>
                    )}
                    {!isRunning && isPending && (
                      <span className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
                        {pendingBadgeLabel}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`text-chip px-1.5 py-0.5 rounded-sm font-mono ${
                        SOURCE_TYPE_STYLE[source.sourceType] ?? 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {source.sourceType}
                    </span>
                    <span className="text-chip text-slate-400 dark:text-slate-500 font-mono">
                      {source.sourceKey}
                    </span>
                  </div>
                  {!isImplemented && (
                    <div className="text-chip text-amber-600 dark:text-amber-400 mt-1">Extracteur planifié</div>
                  )}
                </div>

                <div className="flex-1 space-y-1.5">
                  <div className="flex items-center gap-3 flex-wrap">
                    <StatusPill status={displayStatus} />
                    {!cardShowsRunning && source.lastRunAt && (
                      <span className="text-xs text-slate-400 dark:text-slate-500 tabular-nums">
                        {new Date(source.lastRunAt).toLocaleString('fr-CA', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                      </span>
                    )}
                    {isRunning && runningStatus?.log?.started_at && (
                      <span className="text-xs text-slate-400 dark:text-slate-500 tabular-nums">
                        Démarré à{' '}
                        {new Date(runningStatus.log.started_at).toLocaleTimeString('fr-CA', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    )}
                    {isPending && !isRunning && (
                      <span className="text-xs text-slate-400 dark:text-slate-500">
                        {pendingMode === 'queued'
                          ? 'En attente du worker'
                          : 'Confirmation du démarrage…'}
                      </span>
                    )}
                    {!cardShowsRunning && source.lastRunCount != null && source.lastRunStatus === 'ok' && (
                      <span className="text-xs text-slate-400 dark:text-slate-500">
                        {source.lastRunCount.toLocaleString('fr-CA')} records
                      </span>
                    )}
                    {!cardShowsRunning && source.lastRunMs != null && (
                      <span className="text-xs text-slate-400 dark:text-slate-500 tabular-nums">
                        {source.lastRunMs >= 60000
                          ? `${Math.round(source.lastRunMs / 60000)}m`
                          : `${Math.round(source.lastRunMs / 1000)}s`}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <select
                      value={source.frequency}
                      disabled={patchSource.isPending || anySyncActive}
                      onChange={(e) =>
                        patchSource.mutate({ sourceKey: source.sourceKey, body: { frequency: e.target.value } })
                      }
                      className="text-xs border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300"
                    >
                      {[...new Set([...DEFAULT_FREQS, source.frequency])].map((f) => (
                        <option key={f} value={f}>
                          {FREQ_LABELS[f] ?? f}
                        </option>
                      ))}
                    </select>
                    <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={source.isEnabled}
                        disabled={patchSource.isPending || anySyncActive}
                        className="rounded-sm"
                        onChange={(e) =>
                          patchSource.mutate({ sourceKey: source.sourceKey, body: { isEnabled: e.target.checked } })
                        }
                      />
                      Actif
                    </label>
                  </div>
                </div>

                <div className="shrink-0">
                  {source.sourceKey === 'azure_ad' ? (
                    <span className="text-xs text-slate-400 dark:text-slate-500 italic">Automatique</span>
                  ) : (
                    <RunButton
                      label={buttonLabel}
                      disabled={runSource.isPending || anySyncActive || !source.isEnabled}
                      onClick={() => runSource.mutate(source.sourceKey)}
                      variant={isImplemented ? 'primary' : 'ghost'}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
